import React from 'react';
import StatementHeader from './StatementHeader.jsx';

/* v8 Path AG — shared outer+scroll frame for multi-period tables.
   FormalStatementTable and MetricHorizonTable were hand-rolling the
   same three-layer scaffolding around their <table>:

     <div outer>                           ← rounded border, dark bg
       <StatementHeader .../>              ← entity + title + period
       [defs]                              ← optional SVG defs (SparkDefs)
       <div scrollRef>                     ← the 70vh scroll viewport
         {children}                        ← the <table> itself
       </div>
     </div>

   Repeating this in two places meant every polish pass (Path AA
   restored internal scroll, Path AB added scrollRef, Path X moved
   the header into the frame) had to touch both files and stay in
   sync. Extracting the frame makes it one edit from now on.

   Contract:
   - `ref` — React.forwardRef exposes the SCROLL container (NOT the
     outer wrapper) so callers can feed useStickyCascade with the
     right element. Sticky scopes to the scroll viewport, not the
     outer rounded border.
   - `defs` — optional JSX rendered INSIDE the outer wrapper but
     OUTSIDE the scroll container. SparkDefs lives here so gradient
     defs aren't remounted on every scroll frame. Rendering them
     outside the scroll also keeps them reachable when the viewport
     clips.
   - `children` — the <table>. Callers retain full control of table
     markup (colgroup, thead, tbody) because the two consumers
     diverge heavily below the scroll level.

   Why not also extract the <table>+<colgroup>? Because FormalStatementTable
   has two tbody-shaping functions (shapeIncomeStatement, shapeBalanceSheet,
   shapeCashFlowStatement) that assume direct access to <tbody>, and
   MetricHorizonTable's rows are row-generic (not section-shaped). The
   frame is the true DRY layer; below it, divergence is healthy. */
const StatementFrame = React.forwardRef(function StatementFrame(
  { entity, title, subtitle, period, sourceState, defs, pinnedChrome, children },
  scrollRef
) {
  return (
    <div className="font-sans bg-white/[0.015] border-2 border-border-strong rounded-lg shadow-sm max-w-none mx-auto min-w-0 overflow-hidden relative">
      <StatementHeader entity={entity} title={title} subtitle={subtitle} period={period} sourceState={sourceState} />
      {defs}
      {pinnedChrome && (
        <div
          data-testid="statement-frame-pinned-chrome"
          className="px-4 pt-2 pb-1 bg-[#10131b] border-t border-border border-b border-border/60"
        >
          {pinnedChrome}
        </div>
      )}
      {/* `contain: paint` isolates this container's paints; `will-change:
          scroll` promotes it to its own GPU layer. Both measurably
          reduce scroll jank on dense tables. `max-h-[70vh]` caps the
          scroll region so sticky thead + H1/H2 have a bounded scope. */}
      <div
        ref={scrollRef}
        className="w-full max-w-full min-w-0 overflow-x-auto overflow-y-auto max-h-[70vh] relative [contain:paint] will-change-scroll"
      >
        {children}
      </div>
    </div>
  );
});

export default StatementFrame;
